import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { setDemoLoader } from "./api/adapter";
import { DEMO_AREAS } from "./api/areas";
import { App } from "./app/App";
import { createAppRouter } from "./app/router";
import "@fontsource-variable/geist/wght.css";
import "@fontsource-variable/fraunces/wght.css";
import "@fontsource-variable/fraunces/wght-italic.css";
import "./i18n";
import "./styles/globals.css";

async function start() {
  const arrivedOn = window.location.href; // the first touch (features/growth/touch.ts), read before any redirect
  // Mock mode (VITE_MOCK=1): the in-memory API. Tested on import.meta.env directly (not a shared constant)
  // so the bundler folds it at build time and real builds contain no mock code at all.
  if (import.meta.env.VITE_MOCK === "1") (await import("./mocks")).installMocks();
  // Real build with areas the backend doesn't serve yet: a module's mock loads the first time a screen asks
  // (never in the first load), and those screens show "demo data". `*` means all real: no mock code.
  else if (import.meta.env.VITE_REAL_AREAS !== "*" && DEMO_AREAS.length)
    setDemoLoader(async (area) => {
      await (await import("./mocks/demo")).installDemoMock(area);
    });

  const root = document.getElementById("root");
  if (!root) throw new Error("#root is missing from index.html");
  createRoot(root).render(
    <StrictMode>
      <App router={createAppRouter()} />
    </StrictMode>,
  );
  // Neither is needed to draw the first screen, so they load right after: sound synthesis arms its unlock on
  // the player's first gesture (long before any sound plays), and the service worker registers.
  void import("./lib/sound").then((m) => m.unlockAudio());
  void import("./features/growth/touch").then((m) => {
    m.captureTouch(arrivedOn);
  });
  void import("./pwa/register").then((m) => {
    m.registerServiceWorker();
  });
}

void start();
